// An online tool's page (/<product>/tools/<placement id>): the tool running inside the hub, in the
// product's frame, under Resources (toolRunner.js says how and why). A tool's Open button, on
// Resources, a week's pack or a home's tool card, opens this page in a new tab (model.js toolHref).
// No board draws it, so its words are on the list for Adam.
//
//   <ToolPage product={productRow} route={{ placementId }} />   inside a ProductDataProvider
//
// The page asks sign-file for the tool's current version (a link that lasts 2 minutes, never kept;
// sign-file records the open), reads the file through it, puts the hub's own fonts in and hands it
// to the runner frame. A file not uploaded yet, or refused, shows sign-file's own line. While staff
// view as a member nothing opens (sign-file refuses), as with every file. The tool's own working,
// if it keeps any (localStorage), is kept in this browser for this member only, never while staff
// view as them, and goes when they log out (lib/auth.js). The tool is handed only to the frame this
// page made, once, and only messages carrying this copy's channel are heard (toolRunner.js), so a page
// the frame has navigated to gets nothing and writes nothing.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useMember } from '../app/member.jsx'
import { useNow } from '../lib/clock.js'
import { Link } from '../lib/router.jsx'
import { HubFunctionError } from '../lib/functions.js'
import { useViewingAs, VIEWING_AS_LINE } from '../lib/viewAs.jsx'
import { useProductData } from './ProductData.jsx'
import { itemCard } from './release.js'
import { signPlacement } from './data.js'
import {
  TOOL_ALLOW, TOOL_FRAME_PATH, TOOL_MAX_CHARS, TOOL_SANDBOX, TOOL_SAVED_MAX_CHARS,
  familiesIn, prepareToolHtml, readToolMessage, savedKey, toolChannel,
} from './toolRunner.js'
import { toolFontsCss } from './toolFonts.js'
import '../styles/tool.css'

export const TOOL_FAILED = "The tool couldn't be opened just now. Try again in a minute."
export const BACK_TO_RESOURCES = 'Back to Resources'

// The tool card for a placement of this product, or null when it isn't a tool here
export function toolCard(data, placementId, nowMs) {
  const pl = (data?.placements || []).find(x => x.id === placementId)
  const card = pl ? itemCard(pl, data, nowMs) : null
  return card && card.kind === 'tool' ? card : null
}

function readSaved(key) {
  try {
    const v = JSON.parse(localStorage.getItem(key) || 'null')
    return v && typeof v === 'object' && !Array.isArray(v) ? v : {}
  } catch {
    return {}
  }
}
function writeSaved(key, items) {
  try {
    const text = JSON.stringify(items)
    if (text.length > TOOL_SAVED_MAX_CHARS) return
    if (Object.keys(items).length) localStorage.setItem(key, text)
    else localStorage.removeItem(key)
  } catch { /* storage full or off: the tool works, it just won't remember */ }
}

// The tool, signed, read and ready to hand to the runner: { status, html, channel, line, n }
function useToolHtml(card, personId, viewingAs) {
  const [s, set] = useState({ status: 'loading', html: null, channel: null, line: null, n: 0 })
  const run = useRef(0)
  const load = useCallback(async () => {
    const n = ++run.current
    set({ status: 'loading', html: null, channel: null, line: null, n })
    try {
      const signed = await signPlacement(card.id)
      if (typeof signed?.url !== 'string' || !signed.url.startsWith('https://')) throw new Error('no link')
      const res = await fetch(signed.url, { credentials: 'omit', cache: 'no-store' })
      if (!res.ok) throw new Error(`tool ${res.status}`)
      const raw = await res.text()
      if (!raw.trim() || raw.length > TOOL_MAX_CHARS) throw new Error('tool size')
      const fontsCss = await toolFontsCss(familiesIn(raw)).catch(() => '')
      const saved = viewingAs ? {} : readSaved(savedKey(personId, card.itemId))
      const channel = toolChannel()
      const html = prepareToolHtml(raw, { hubOrigin: window.location.origin, saved, fontsCss, channel })
      if (n === run.current) set({ status: 'ready', html, channel, line: null, n })
    } catch (err) {
      if (n === run.current) set({ status: 'failed', html: null, channel: null, line: err instanceof HubFunctionError ? err.message : TOOL_FAILED, n })
    }
  }, [card?.id, card?.itemId, personId, viewingAs])
  useEffect(() => {
    if (!card || !card.open || viewingAs) return undefined
    load()
    return () => { run.current++ }
  }, [load, card?.open, viewingAs])
  return { ...s, reload: load }
}

export function ToolPage({ product, route }) {
  const { me } = useMember()
  const { data } = useProductData()
  const nowMs = useNow()
  const viewingAs = useViewingAs()
  const p = data?.product || product
  const card = useMemo(() => toolCard(data, route.placementId, nowMs), [data, route.placementId, nowMs])
  const tool = useToolHtml(card, me.person.id, viewingAs)
  const back = `${p.path || ''}/resources`

  if (!card) return null
  return (
    <section className="tl" aria-labelledby="tl-title">
      <div className="tl-head">
        <Link className="tl-back" to={back}>{BACK_TO_RESOURCES}</Link>
        <h1 className="tl-h1" id="tl-title">{card.title}</h1>
        {card.meta && <p className="tl-meta">{card.meta}</p>}
      </div>
      {!card.open && <p className="tl-line">{card.when}</p>}
      {card.open && viewingAs && <p className="tl-line">{VIEWING_AS_LINE}</p>}
      {card.open && !viewingAs && tool.status === 'loading' && <div className="tl-loading" aria-busy="true" />}
      {card.open && !viewingAs && tool.status === 'failed' && (
        <div className="tl-failed">
          <p className="tl-line" role="alert">{tool.line}</p>
          <button type="button" className="pp-btn" onClick={tool.reload}>Try again</button>
        </div>
      )}
      {card.open && !viewingAs && tool.status === 'ready' && (
        <ToolFrame key={tool.n} html={tool.html} channel={tool.channel} title={card.title}
          keep={items => writeSaved(savedKey(me.person.id, card.itemId), items)} />
      )}
    </section>
  )
}

// The runner frame. It asks for the tool once it's ready, and gets it once; after that only the tool's
// own messages count, each carrying this copy's channel. A frame that has navigated away is the same
// window, so it could ask again or claim to be the tool: it gets nothing and writes nothing.
export function ToolFrame({ html, channel, title, keep }) {
  const ref = useRef(null)
  const keepRef = useRef(keep)
  keepRef.current = keep
  const [height, setHeight] = useState(null)
  useEffect(() => {
    let handed = false
    function onMessage(e) {
      const frame = ref.current
      if (!frame || e.source !== frame.contentWindow) return
      const msg = readToolMessage(e.data, channel)
      if (!msg) return
      if (msg.kind === 'ready') {
        if (handed) return
        handed = true
        // the runner has no origin of its own, so the tool goes to it by the frame, not an address
        frame.contentWindow.postMessage({ hubTool: 'run', html }, '*')
      } else if (!handed) {
        return
      } else if (msg.kind === 'height') setHeight(msg.height + 8)
      else if (msg.kind === 'storage') keepRef.current?.(msg.items)
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [html, channel])
  return (
    <div className="tl-frame">
      <iframe ref={ref} src={TOOL_FRAME_PATH} title={title} sandbox={TOOL_SANDBOX} allow={TOOL_ALLOW}
        referrerPolicy="no-referrer" style={height ? { height: `${height}px` } : undefined} />
    </div>
  )
}
